import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { ListRegulation, SignUpRequest } from '@zelos/codegen/apis';
import { V1Service } from '@zelos/codegen/apis';
import { regex } from '@zelos/shared/constants/regex';
import { resetAtomAccessCookie } from '@zelos/shared/lib/access-cookie';
import { redirectTo } from '@zelos/shared/lib/redirect-util';
import {
  setReactHookFormErrorByErrors,
  throwTypedApiError,
  toTypedApiError,
} from '@zelos/shared/lib/typed-api-error';
import produce from 'immer';
import { useAtom } from 'jotai';
import Image from 'next/image';
import { useRouter } from 'next/router';
import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import {
  InputAboveDiv,
  InputWrapDiv,
  InputWrapSpan,
} from '../../SignInput';
import SnsSign from '../../SnsSign';
import Regulation from './Regulation';
import RegulationModal from './RegulationModal';
import { useGuardianModal } from './use-guardian-modal';
import Button from '@/components/comm/Button';
import Checkbox from '@/components/comm/Checkbox';
import Divider from '@/components/comm/Divider';
import ImageButton from '@/components/comm/ImageButton';
import Input, { ErrorWrapDiv } from '@/components/comm/Input';
import Radio from '@/components/comm/Radio';
import RequiredAsterisk from '@/components/comm/RequiredAsterisk';
import { tempFindUserAtom } from '@/components/pages/sign';
import { breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import {
  createErrorToast,
  showToast,
} from '@/lib/toast';
import icEyeSlash from '@/public/images/ic-eye-slash.svg';
import icEye from '@/public/images/ic-eye.svg';
import icFace from '@/public/images/ic-face.svg';
import icLock from '@/public/images/ic-lock.svg';
import icMessage from '@/public/images/ic-message.svg';
import icNotice from '@/public/images/ic-notice.svg';
interface SignUpType extends SignUpRequest {
  passwordCheck: string;
  needGuardian: boolean | null;
}

interface CheckRegulation extends ListRegulation {
  checked: boolean;
}

const Container = styled.div`
display: flex;
flex-direction: column;
align-items: center;
padding: 5px 32px;

@media (max-width: ${breakpointSmall}) {
  padding: 5px 20px;
}
`;

const FormStyled = styled.form`
width: 100%;
`;

const CheckBoxWrapDiv = styled.div`
display: grid;
width: 100%;
margin-top: 55px;
row-gap: 13px;
`;

const SingUpButton = styled(Button)`
width: 100%;
height: 54px;
margin-top: 54px;
`;

const RadioWrapDiv = styled.div`
display: flex;
justify-content: space-between;
gap: 12px;
@media (max-width: ${breakpointSmall}) {
  flex-direction: column;
}
`;
const RadioDiv = styled.div<{ checked?: boolean }>`
display: flex;
flex: 1;
flex-direction: row;
align-items: flex-start;
padding: 16px;
border: 1px solid
  ${({ checked }) =>
    checked ? colors.blue : colors.lightGrey2};
background:
  ${({ checked }) =>
    checked
      ? 'linear-gradient(0deg, rgba(0, 120, 255, 0.04) 0%, rgba(0, 120, 255, 0.04) 100%), #FFF'
      : 'none'};
border-radius: 4px;
cursor: pointer;
gap: 8px;
`;
const RadioInputContent = styled.div`
display: flex;
flex: 1;
flex-direction: column;
font-style: normal;
font-weight: 400;
`;
const RadioInputTitle = styled.span`
color: ${colors.indigo};
font-size: 16px;
line-height: 150%;
`;
const RadioInputDescription = styled.span`
color: ${colors.lightSilver};
font-size: 12px;
line-height: 160%;
`;

interface RadioInputCardProps {
  checked: boolean;
  onClick: () => void;
  radioProps: React.ComponentProps<typeof Radio>;
  title: string;
  description?: string;
}
const RadioInputCard = ({ checked, onClick, radioProps, title, description }: RadioInputCardProps) => {
  const [hovered, setHovered] = useState(false);
  return (
    <RadioDiv
      checked={checked}
      onClick={onClick}
      tabIndex={0}
      role="button"
      aria-pressed={checked}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Radio
        {...radioProps}
        checked={checked}
        isHovered={hovered}
        style={{ marginTop: '4px', pointerEvents: 'none' }}
        tabIndex={-1}
      />
      <RadioInputContent>
        <RadioInputTitle>{title}</RadioInputTitle>
        {description && <RadioInputDescription>{description}</RadioInputDescription>}
      </RadioInputContent>
    </RadioDiv>
  );
};

const SignUpForm = () => {
  const [{ email }, setTempFindUser] = useAtom(tempFindUserAtom);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignUpType>();
  const watchPassword = watch('password', '');

  const { data: regulationsList } = useQuery(
    ['v1RegulationsList', { scope: 'URE' }],
    async () => {
      const regulationsList = await V1Service.v1RegulationsList({ scope: 'URE' });
      regulationsList.sort(
        (a, b) => -(Number(a.isRequired || false) - Number(b.isRequired || false)) || a.id - b.id,
      );

      return regulationsList;
    },
    { refetchOnWindowFocus: false },
  );

  const [checkRegulationsList, setCheckRegulationsList] = useState<CheckRegulation[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [needGuardian, setNeedGuardian] = useState<boolean | null>(null);

  const { GuardianModal, showGuardianModal } = useGuardianModal();

  const signUp = useCallback(async ({ email, name, password, isMinor }: SignUpType) => {
    try {
      const marketingAgreement = checkRegulationsList.find(
        ({ name }) => /marketing|마케팅/i.test(name),
      )?.checked || false;

      await V1Service.v1UsersSignUpCreate({
        requestBody: {
          name,
          email,
          password,
          marketingAgreement,
          isMinor,
        },
      }).catch(throwTypedApiError);
      resetAtomAccessCookie();

      showToast({ text: '회원 가입에 성공하였습니다.' });
      await V1Service.v1LogsActionsTrackingsCreate({
        requestBody: {
          url: window.location.href,
          actionTrackingEvent: 30,
        },
      });
      redirectTo(router.query.destination ? decodeURIComponent(String(router.query.destination)) : '/');
    } catch (error) {
      const errorBody = toTypedApiError(error);
      if (errorBody) {
        setReactHookFormErrorByErrors(setError, errorBody.errors);
        createErrorToast(errorBody);
      } else {
        throw error;
      }
    }
  }, [checkRegulationsList, router.query.destination, setError]);

  const signUpSubmit = useCallback((signUpData: SignUpType) => {
    if (checkRegulationsList.some(({ isRequired, checked }) => isRequired && !checked)) {
      showToast({ text: '필수 항목을 체크해 주세요' });
      return;
    }

    needGuardian
      ? showGuardianModal(() => signUp(produce(signUpData, (draft) => {
        draft.isMinor = true;
      })))
      : signUp(signUpData);

  }, [checkRegulationsList, showGuardianModal, signUp, needGuardian]);

  useEffect(
    () => () => setTempFindUser({}),
    [setTempFindUser],
  );

  useEffect(() => {
    if (!regulationsList) return;

    setCheckRegulationsList(
      regulationsList.filter((checkRegulation) => {
        return !checkRegulation.name.includes('14세'); // 서버에서 전달하는 체크박스 목록 중 14세 미만 부분 노출 제외.
      }).map((checkRegulation) =>
        ({ ...checkRegulation, checked: false })),
    );
  }, [regulationsList]);

  return (
    <Container>
      <FormStyled
        noValidate
        onSubmit={handleSubmit(signUpSubmit)}
      >
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>이름</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            type="text"
            placeholder="이름을 입력해 주세요"
            error={errors.name}
            {...register('name', {
              required: '이름을 입력해 주세요',
              pattern: {
                value: regex.name,
                message: '올바른 이름을 입력해 주세요.',
              },
            })}
            start={<Image src={icFace} alt="face" />}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>이메일</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            type="email"
            placeholder="이메일을 입력해 주세요"
            defaultValue={email}
            error={errors.email}
            {...register('email', {
              required: '이메일을 입력해 주세요',
              pattern: {
                value: regex.email,
                message: '올바르지 않은 이메일 형식입니다.',
              },
              validate: {
                async asyncValidate(value) {
                  try {
                    const { isDuplicate } = await V1Service.v1UsersEmailDuplicateCreate({ requestBody: { email: value } });
                    return !isDuplicate || '중복된 이메일입니다.';
                  } catch {
                    return '이메일 확인 중 오류가 발생했습니다.';
                  }
                },
              },
            })}
            start={<Image src={icMessage} alt="message" />}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>비밀번호</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="(문자, 숫자, 특수문자 포함 최소 8자)"
            autoComplete={showPassword ? 'off' : 'on'}
            error={errors.password}
            {...register('password', {
              required: '비밀번호를 입력해 주세요',
              pattern: {
                value: regex.password,
                message: '문자, 숫자, 특수문자 포함 최소 8자를 입력해 주세요',
              },
            })}
            start={<Image src={icLock} alt="lock" />}
            end={(
              <ImageButton
                onClick={() => setShowPassword(!showPassword)}
                src={showPassword ? icEye : icEyeSlash}
                alt="show password"
                width={24}
                height={17}
                style={{
                  objectFit: 'contain',
                }}
              />
            )}
          />
          <Input
            type={showPasswordConfirm ? 'text' : 'password'}
            placeholder="비밀번호를 확인해 주세요"
            autoComplete={showPasswordConfirm ? 'off' : 'on'}
            error={errors.passwordCheck}
            {...register('passwordCheck', {
              required: '비밀번호를 확인해 주세요',
              pattern: {
                value: regex.password,
                message: '문자, 숫자, 특수문자 포함 최소 8자를 입력해 주세요',
              },
              validate: {
                positive: (value) => value === watchPassword || '비밀번호가 일치하지 않습니다.',
              },
            })}
            start={<Image src={icLock} alt="lock" />}
            end={(
              <ImageButton
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                src={showPasswordConfirm ? icEye : icEyeSlash}
                alt="show password comfirm"
                width={24}
                height={17}
                style={{
                  objectFit: 'contain',
                }}
              />
            )}
          />
        </InputWrapDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>
              <span>연령대 선택</span>
              <RequiredAsterisk />
            </InputWrapSpan>
          </InputAboveDiv>
          <RadioWrapDiv>
            <RadioInputCard
              checked={needGuardian !== null && !needGuardian}
              onClick={() => setNeedGuardian(false)}
              radioProps={register('needGuardian', { required: '연령대를 선택해 주세요.' })}
              title="만 14세 이상입니다."
              description="&nbsp;"
            />
            <RadioInputCard
              checked={needGuardian !== null && needGuardian}
              onClick={() => setNeedGuardian(true)}
              radioProps={register('needGuardian', { required: '연령대를 선택해 주세요.' })}
              title="만 14세 미만입니다."
              description="보호자 동의가 필요합니다."
            />
          </RadioWrapDiv>
          {errors.needGuardian && (
            <ErrorWrapDiv style={{ marginTop: 0 }}>
              <Image src={icNotice} alt="notice" />
              <span>{errors.needGuardian.message}</span>
            </ErrorWrapDiv>
          )}
        </InputWrapDiv>
        <CheckBoxWrapDiv>
          <Checkbox
            boxStyle={{
              fontSize: 14,
              fontWeight: 700,
            }}
            checked={checkRegulationsList.every(({ checked }) => checked)}
            onChange={() => {
              const isWillCheckAll = !checkRegulationsList.every(({ checked }) => checked);
              setCheckRegulationsList(
                checkRegulationsList.map((checkRegulation) =>
                  ({ ...checkRegulation, checked: isWillCheckAll })),
              );
            }}
          >전체 동의
          </Checkbox>
          <Divider color={colors.lightGrey2} />
          {checkRegulationsList.map((regulation, index) => (
            <Checkbox
              boxStyle={{
                color: colors.grey4,
                fontSize: 13,
                fontWeight: 400,
                lineHeight: 19,
              }}
              key={regulation.id}
              checked={regulation.checked}
              onChange={() => {
                setCheckRegulationsList(produce((draft) => {
                  draft[index].checked = !regulation.checked;
                }));
              }}
            >
              <Regulation regulation={regulation} />
            </Checkbox>
          ))}
          <RegulationModal />
          <GuardianModal />
        </CheckBoxWrapDiv>
        <SingUpButton type="submit">회원가입 하기</SingUpButton>
      </FormStyled>
      <SnsSign text="다른 계정으로 가입하기" />
    </Container>
  );
};

export default SignUpForm;
